// Shared formatting helpers for the FieldPilot schedule panel and exports.
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { expectedFractionAt, parseDateKey } from "@constructionautomation/backend/convex/fieldPilot/schedule";
import type { StatusBadgeTone, StatusKey } from "@/components/status-badge";

/**
 * The time-dependent schedule fields shared by schedule rows and their nested sub-tasks. Kept
 * separate so `withFreshScheduleTiming` can refresh either shape.
 */
export interface FieldPilotScheduleTiming {
  durationDays: number | null;
  startDateKey: string | null;
  finishDateKey: string | null;
  actualFraction: number;
  expectedFraction: number;
  finishVarianceDays: number | null;
  isBehind: boolean;
  hasSchedule: boolean;
  progressMethod?: "completion_statuses" | "milestones";
  plannedCheckpoints?: { dateKey: string; fraction: number }[];
}

export interface FieldPilotScheduleRow extends FieldPilotScheduleTiming {
  tagId: Id<"fieldPilotTags">;
  rawValue: string;
  displayName: string;
  isCustom: boolean;
}

/** Status metadata used to render a schedule health indicator. */
export interface ScheduleStatusDot {
  status: StatusKey;
  tone?: StatusBadgeTone;
  label: string;
}

interface ScheduleStatusInput {
  hasSchedule: boolean;
  isBehind: boolean;
}

/** On-track indicator shared by the modal, the main schedule columns, and visualizations. */
export function scheduleStatusDot(row: ScheduleStatusInput): ScheduleStatusDot {
  if (!row.hasSchedule) {
    return { status: "pending", tone: "muted", label: "No schedule dates set" };
  }
  if (row.isBehind) {
    return { status: "error", label: "Behind schedule" };
  }
  return { status: "completed", label: "On track" };
}

/** Format a calendar-day count as weeks (>= 7 days) or days, e.g. "3 wks" / "5 d". */
export function formatDurationDays(days: number | null | undefined): string {
  if (days === null || days === undefined || !Number.isFinite(days)) return "—";
  if (days === 0) return "0 d";
  if (Math.abs(days) >= 7) {
    const weeks = days / 7;
    return `${weeks.toFixed(weeks % 1 === 0 ? 0 : 1)} wks`;
  }
  return `${days} d`;
}

/** Format a signed finish-variance day count. Negative = ahead, positive = behind. */
export function formatVarianceDays(days: number | null | undefined): string {
  if (days === null || days === undefined || !Number.isFinite(days)) return "—";
  if (days === 0) return "On time";
  const magnitude = formatDurationDays(Math.abs(days));
  return days > 0 ? `${magnitude} late` : `${magnitude} early`;
}

/** Format a 0..1 fraction as a whole-number percentage. */
export function formatSchedulePercent(fraction: number | null | undefined): string {
  if (fraction === null || fraction === undefined || !Number.isFinite(fraction)) {
    return "—";
  }
  return `${Math.round(fraction * 100)}%`;
}

/** Parse a YYYY-MM-DD key to a local-midnight timestamp, or null when unset/malformed. */
export function parseDateKeyToMs(dateKey: string | null | undefined): number | null {
  if (!dateKey) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  // Reject impossible calendar dates (e.g. 2026-02-31) that Date would roll forward.
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  const ms = date.getTime();
  return Number.isFinite(ms) ? ms : null;
}

/** Format a YYYY-MM-DD key as M/D/YY; returns an em dash when unset/malformed. */
export function formatScheduleDate(dateKey: string | null | undefined): string {
  const ms = parseDateKeyToMs(dateKey);
  if (ms === null) return "—";
  const date = new Date(ms);
  return `${date.getMonth() + 1}/${date.getDate()}/${String(date.getFullYear() % 100).padStart(2, "0")}`;
}

/**
 * Sort comparator placing rows with a start date first (ascending), then undated rows by name.
 */
export function compareScheduleRows(
  a: { startDateKey: string | null; displayName: string },
  b: { startDateKey: string | null; displayName: string }
): number {
  const aMs = parseDateKeyToMs(a.startDateKey);
  const bMs = parseDateKeyToMs(b.startDateKey);
  // Base sensitivity matches Python casefold() used by the PDF Gantt sorter.
  const byName = () => a.displayName.localeCompare(b.displayName, undefined, { sensitivity: "base" });
  if (aMs !== null && bMs !== null) {
    if (aMs !== bMs) return aMs - bMs;
    return byName();
  }
  if (aMs !== null) return -1;
  if (bMs !== null) return 1;
  return byName();
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function clampFraction(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

/**
 * Recompute the time-dependent schedule fields against the client clock.
 *
 * `getScheduleByJob` captures `Date.now()` once at query execution, and Convex queries only
 * re-run when their database reads change — not as the day advances. Recomputing
 * `expectedFraction`, `isBehind`, `durationDays`, and `finishVarianceDays` from the stable dated
 * progress checkpoints plus the server-provided `actualFraction` keeps the panel fresh between
 * data changes. Milestone rows therefore retain their piecewise milestone plan instead of falling
 * back to a simple elapsed-time percentage on the client.
 */
export function withFreshScheduleTiming<T extends FieldPilotScheduleTiming>(
  row: T,
  now: number = Date.now()
): T {
  // Compare local wall-clock time with timezone-free schedule dates. Keep calendar
  // days equally weighted, including schedules that cross a DST change.
  now -= new Date(now).getTimezoneOffset() * 60_000;
  const startMs = parseDateKey(row.startDateKey);
  const finishMs = parseDateKey(row.finishDateKey);
  const hasSchedule = startMs !== null && finishMs !== null && finishMs >= startMs;
  if (!hasSchedule || startMs === null || finishMs === null) {
    return {
      ...row,
      durationDays: null,
      expectedFraction: 0,
      finishVarianceDays: null,
      isBehind: false,
      hasSchedule: false,
    };
  }

  const spanMs = finishMs - startMs;
  const durationDays = Math.round(spanMs / MS_PER_DAY);
  const checkpoints = row.plannedCheckpoints?.length
    ? row.plannedCheckpoints
    : [
        { dateKey: row.startDateKey as string, fraction: 0 },
        { dateKey: row.finishDateKey as string, fraction: 1 },
      ];
  const expectedFraction = expectedFractionAt(checkpoints, now);

  const actualFraction = clampFraction(row.actualFraction);
  const elapsedMs = now - startMs;
  let projectedFinishMs: number | null = null;
  if (actualFraction >= 1) {
    projectedFinishMs = finishMs;
  } else if (elapsedMs > 0 && actualFraction > 0) {
    projectedFinishMs = startMs + elapsedMs / actualFraction;
  }
  const finishVarianceDays =
    projectedFinishMs === null
      ? null
      : Math.round((projectedFinishMs - finishMs) / MS_PER_DAY);

  return {
    ...row,
    durationDays,
    expectedFraction,
    finishVarianceDays,
    isBehind: actualFraction + 1e-9 < expectedFraction,
    hasSchedule: true,
  };
}

/**
 * Compute the inclusive selectable date range for a milestone at `targetRank`, given the other
 * dated milestones in the same pipeline. A milestone cannot be due before an earlier-pipeline
 * milestone, nor after a later-pipeline one (the backend enforces this; these bounds prevent the
 * out-of-order selection up front). Date keys are `YYYY-MM-DD`, so lexicographic order == time
 * order. The milestone at `targetRank` itself is ignored.
 */
export function milestoneDateBounds(
  datedMilestones: readonly { rank: number; dueDateKey: string | null }[],
  targetRank: number
): { min?: string; max?: string } {
  let min: string | undefined;
  let max: string | undefined;
  for (const milestone of datedMilestones) {
    if (!milestone.dueDateKey) continue;
    if (milestone.rank < targetRank) {
      if (min === undefined || milestone.dueDateKey > min) min = milestone.dueDateKey;
    } else if (milestone.rank > targetRank) {
      if (max === undefined || milestone.dueDateKey < max) max = milestone.dueDateKey;
    }
  }
  return { min, max };
}
